import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Tag, Avatar } from "@ann/web-components";
import { ActivityLogContainer, UserCellContainer, UserAvatarContainer, UserTextContainer } from "./ActivityLog.styled";
import { ActivityLogTableSection } from "./components";
import { getAuditList } from "../../../../../../../services";
import { useDebounce } from "../../../../../../../hooks";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";

// Extend dayjs with plugins
dayjs.extend(isToday);
dayjs.extend(isYesterday);

const ActivityLog = () => {
  const { knowledgeId } = useParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("created_at");
  const [orderDirection, setOrderDirection] = useState("DESC");
  const [loading, setLoading] = useState(false);
  const [activities, setActivities] = useState([]);
  const [meta, setMeta] = useState({
    totalItems: 0,
    totalPages: 0,
    currentPage: 0,
    perPage: 0,
  });

  // Filter states
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedActions, setSelectedActions] = useState([]);

  // Debounce search keyword
  const debouncedSearchKeyword = useDebounce(searchKeyword, 500);

  // Track if search is pending (user is typing but debounce hasn't fired yet)
  const isSearching = searchKeyword !== debouncedSearchKeyword;

  // Format timestamp: "Today, 10:30AM", "Yesterday, 10:30AM", "Dec 15, 11:30 PM"
  const formatTimestamp = useCallback((dateString) => {
    if (!dateString) return "—";

    const date = dayjs(dateString);
    const timeFormat = date.format("h:mm A");

    if (date.isToday()) {
      return `Today, ${timeFormat}`;
    }

    if (date.isYesterday()) {
      return `Yesterday, ${timeFormat}`;
    }

    // For older dates: "Dec 15, 11:30 PM"
    return date.format("MMM D, h:mm A");
  }, []);

  // Get action color for Tag
  const getActionColor = useCallback((action) => {
    const normalizedAction = typeof action === 'string' ? action.toLowerCase() : '';
    
    switch (normalizedAction) {
      case 'created':
        return 'success';
      case 'updated':
      case 'modified':
        return 'info';
      case 'deleted':
        return 'error';
      default:
        return 'neutral';
    }
  }, []);

  // Generate avatar URL using ui-avatars.com service
  const getAvatarUrl = useCallback((userName) => {
    if (!userName || userName === "—") {
      return null;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}`;
  }, []);

  // Fetch activity log from API
  useEffect(() => {
    const fetchActivityLog = async () => {
      setLoading(true);
      try {
        // Map orderBy from component format to API format
        const orderByMap = {
          timestamp: "created_at",
          fact: "section",
          user: "user_full_name",
          action: "action",
        };
        const apiOrderBy = orderByMap[orderBy] || "created_at";

        // Build search object if keyword is provided
        const searchObj = debouncedSearchKeyword
          ? {
              keyword: debouncedSearchKeyword,
            }
          : undefined;

        // Build filters object
        const filtersObj = {};
        if (selectedActions && selectedActions.length > 0) {
          // Take first action if array, or use directly if string
          filtersObj.action = Array.isArray(selectedActions) ? selectedActions[0] : selectedActions;
        }
        
        // Optionally filter by knowledge base section if knowledgeId is available
        if (knowledgeId) {
          filtersObj.section_uuid = knowledgeId;
        }

        const response = await getAuditList({
          page: currentPage,
          page_size: itemsPerPage,
          order_by: apiOrderBy,
          order_direction: orderDirection,
          search: searchObj,
          filters: Object.keys(filtersObj).length > 0 ? filtersObj : undefined,
        });

        if (response && response.body) {
          // Map API response to component format
          const mappedActivities = response.body.map((audit) => ({
            id: audit.created_at || audit.request_id || `audit-${Date.now()}`,
            fact: audit.item || audit.section || "—",
            action: audit.action || "—",
            details: audit.description || "—",
            user_full_name: audit.user_full_name,
            userFullName: audit.user_full_name,
            userName: audit.user_full_name,
            user_avatar_url: audit.user_avatar_url,
            userAvatarUrl: audit.user_avatar_url,
            created_at: audit.created_at,
            createdAt: audit.created_at,
            timestamp: audit.created_at,
            section: audit.section,
            outcome: audit.outcome,
          }));

          setActivities(mappedActivities);
          if (response.meta) {
            setMeta({
              totalItems: response.meta.total_items || 0,
              totalPages: response.meta.total_pages || 0,
              currentPage: currentPage,
              perPage: response.meta.per_page || itemsPerPage,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching activity log:", error);
        setActivities([]);
        // TODO: Show error toast
      } finally {
        setLoading(false);
      }
    };

    fetchActivityLog();
  }, [knowledgeId, currentPage, itemsPerPage, orderBy, orderDirection, debouncedSearchKeyword, selectedActions]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback((items) => {
    setItemsPerPage(items);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((selectedOption) => {
    const validOrderBy = ["timestamp", "fact", "user", "action"];
    const newOrderBy = validOrderBy.includes(selectedOption) ? selectedOption : "timestamp";
    setOrderBy(newOrderBy);
    setCurrentPage(1);
  }, []);

  const handleOrderChange = useCallback((currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((keyword) => {
    setSearchKeyword(keyword);
    setCurrentPage(1);
  }, []);

  const handleActionChange = useCallback((action) => {
    setSelectedActions(action ? [action] : []);
    setCurrentPage(1);
  }, []);

  // Map API response to table data
  const tableData = activities.map((activity) => {
    const actionText = activity.action || "—";
    const actionDisplay = actionText !== "—"
      ? actionText.charAt(0).toUpperCase() + actionText.slice(1).toLowerCase()
      : "—";

    return {
      id:
        activity.id ||
        activity.activityUuid ||
        activity.created_at ||
        activity.timestamp,
      fact: activity.fact || "—",
      action: (
        <Tag
          color={getActionColor(actionText)}
          size="small"
          text={actionDisplay}
        />
      ),
      details: activity.details || "—",
      user: (() => {
        const userName = activity.user_full_name || activity.userFullName || activity.userName || "—";
        
        if (userName === "—") {
          return "—";
        }

        // Use avatar URL from API/mock, fallback to generated URL if not provided
        const avatarUrl = activity.user_avatar_url || activity.userAvatarUrl || getAvatarUrl(userName);

        return (
          <UserCellContainer>
            <UserAvatarContainer>
              <Avatar
                size="md"
                src={avatarUrl}
                alt={userName}
              />
            </UserAvatarContainer>
            <UserTextContainer>
              {userName}
            </UserTextContainer>
          </UserCellContainer>
        );
      })(),
      timestamp: formatTimestamp(activity.created_at || activity.createdAt || activity.timestamp),
    };
  });

  const headers = [
    { label: 'Fact', value: 'fact' },
    { label: 'Action', value: 'action' },
    { label: 'Details', value: 'details' },
    { label: 'User', value: 'user' },
    { label: 'Timestamp', value: 'timestamp' }
  ];

  const getSelectedSortOption = () => {
    const validOrderBy = ["timestamp", "fact", "user", "action"];
    return validOrderBy.includes(orderBy) ? orderBy : "timestamp";
  };

  const getOrderDirection = () => {
    return orderDirection.toLowerCase();
  };

  return (
    <ActivityLogContainer>
      <ActivityLogTableSection
        tableData={tableData}
        headers={headers}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={handleItemsPerPageChange}
        onPageChange={handlePageChange}
        totalItems={meta.totalItems || 0}
        totalPages={meta.totalPages || 0}
        loading={loading || isSearching}
        onSortChange={handleSortChange}
        onOrderChange={handleOrderChange}
        selectedSortOption={getSelectedSortOption()}
        orderDirection={getOrderDirection()}
        onSearchChange={handleSearchChange}
        onActionChange={handleActionChange}
      />
    </ActivityLogContainer>
  );
};

export default ActivityLog;
